import React from 'react';
import PropTypes from 'prop-types';
import dateFormat from 'dateformat';
import classNames from 'classnames';
import { compact, isNil } from 'lodash-es';
import { Collapse } from 'site-modules/shared/components/collapse/collapse';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { getPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { numberWithCommasV2 } from 'site-modules/shared/utils/string';

import './about-lease.scss';

function getMileageInsightText(mileage) {
  if (isNil(mileage)) return null;
  if (mileage < 10000) {
    return "This lease's mileage allowance is lower than typical. This works best if you mostly drive locally, work from home, or rely on another vehicle for longer trips. Note that overage charges usually run between $0.10 to $0.30 per mile.";
  }
  if (mileage <= 12000) {
    return 'This is a standard mileage allowance and works for most drivers. If you drive frequently or take long trips regularly, you may want a higher mileage allowance to avoid extra fees at the end of the lease. Overage charges usually run between $0.10 to $0.30 per mile.';
  }
  return "This lease includes a higher mileage allowance than most offers. It's ideal for frequent drivers or drivers with long commutes and can help reduce excess mileage charges at the end of the lease. Note that overage charges usually run between $0.10 to $0.30 per mile.";
}

export function AboutLease({
  term,
  dueAtSigning,
  mileageYearlyExcess = undefined,
  trim = undefined,
  endDate,
  lifetimeCost = undefined,
}) {
  const [isMileageExpanded, toggleMileage] = useToggle(false);

  const formattedDueAtSigning = getPriceStringV2(dueAtSigning, 'Contact dealer', { allowZero: true });
  const formattedYearlyMileage = isNil(mileageYearlyExcess) ? null : numberWithCommasV2(mileageYearlyExcess);
  const formattedLifetimeCost = getPriceStringV2(lifetimeCost, 'Not Available');

  const leaseInfo = compact([
    { label: 'Term', value: `${term} months` },
    { label: 'Due at signing', value: formattedDueAtSigning },
    {
      label: 'Mileage allowance',
      value: !formattedYearlyMileage ? 'Not Available' : `${formattedYearlyMileage} mi/yr`,
    },
    { label: 'Lifetime cost', value: formattedLifetimeCost },
    trim ? { label: 'Eligible trims', value: trim } : undefined,
    { label: 'Offer ends', value: dateFormat(endDate, 'mmm. d, yyyy') },
  ]);

  return (
    <div className="bg-cool-gray-100 rounded-24 p-1_5 mb-1 mb-md-0 about-lease">
      <h3 className="size-20 fw-medium mb-1 text-cool-gray-10">About this lease</h3>
      <ul className="list-unstyled mb-0">
        {leaseInfo.map(({ label, value }, index, array) => {
          const isMileageRow = label === 'Mileage allowance';
          return (
            <li
              key={label}
              className={classNames('py-1', {
                'border-bottom': index !== array.length - 1,
              })}
            >
              <div className="d-flex justify-content-between">
                <span className="text-cool-gray-40">{label}</span>
                {isMileageRow && formattedYearlyMileage ? (
                  <button
                    type="button"
                    className="d-flex align-items-center gap-0_75 p-0 bg-transparent border-0 fw-bold text-cool-gray-30 btn-link text-transform-none"
                    onClick={toggleMileage}
                    aria-expanded={isMileageExpanded}
                    data-tracking-id="expand_info"
                    data-tracking-value="Mileage Allowance"
                  >
                    {value}
                    <span
                      className={classNames('icon size-12', {
                        'icon-arrow-down3': !isMileageExpanded,
                        'icon-arrow-up3': isMileageExpanded,
                      })}
                      aria-hidden
                    />
                  </button>
                ) : (
                  <strong className="text-cool-gray-30">{value}</strong>
                )}
              </div>
              {isMileageRow && (
                <Collapse isOpen={isMileageExpanded}>
                  <p className="size-14 text-cool-gray-40 mt-0_5 mb-0">{getMileageInsightText(mileageYearlyExcess)}</p>
                </Collapse>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

AboutLease.propTypes = {
  term: PropTypes.number.isRequired,
  dueAtSigning: PropTypes.number.isRequired,
  endDate: PropTypes.string.isRequired,
  mileageYearlyExcess: PropTypes.number,
  trim: PropTypes.string,
  lifetimeCost: PropTypes.number,
};
